<template>
  <div class="warehouse-page">
    <div class="nav-bar-operations">
      <img src="@/assets/logogruas.png" alt="HomePage" @click="backMainPanel" class="logo-button" />
    </div>

    <div class="page-content">
      <div class="page-header">
        <div>
          <h2>Préstamo de herramienta</h2>
          <p class="page-subtitle">Entrega al mecánico y devolución a almacén · {{ totalCount }} vales</p>
        </div>
        <div class="header-actions">
          <button v-if="canCreate" class="action-button action-primary" @click="openCreateModal">Nuevo préstamo</button>
          <button class="action-button" @click="backWarehouse">Volver a Almacén</button>
        </div>
      </div>

      <div class="filters-card">
        <div class="filter-group">
          <label for="filter-ph-folio">Folio</label>
          <input id="filter-ph-folio" type="text" v-model="filters.voucher_number" @keyup.enter="applyFilters" placeholder="PH-1" />
        </div>
        <div class="filter-group">
          <label for="filter-ph-part">Herramienta</label>
          <input id="filter-ph-part" type="text" v-model="filters.part" @keyup.enter="applyFilters" placeholder="Nombre o código" />
        </div>
        <div class="filter-group">
          <label for="filter-ph-mechanic">Mecánico</label>
          <input id="filter-ph-mechanic" type="text" v-model="filters.mechanic" @keyup.enter="applyFilters" />
        </div>
        <div class="filter-group">
          <label for="filter-ph-status">Estatus</label>
          <select id="filter-ph-status" v-model="filters.status">
            <option value="">Todos</option>
            <option value="loaned">Prestada</option>
            <option value="overdue">Vencida</option>
            <option value="returned">Devuelta</option>
            <option value="cancelled">Cancelada</option>
          </select>
        </div>
        <div class="filter-group">
          <label for="filter-ph-from">Desde</label>
          <input id="filter-ph-from" type="date" v-model="filters.start_date" />
        </div>
        <div class="filter-group">
          <label for="filter-ph-to">Hasta</label>
          <input id="filter-ph-to" type="date" v-model="filters.end_date" />
        </div>
        <div class="filter-buttons">
          <button class="action-button action-primary" @click="applyFilters">Buscar</button>
          <button class="action-button" @click="clearFilters">Limpiar</button>
        </div>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Entrega</th>
                <th>Mecánico</th>
                <th>Devolución esperada</th>
                <th>Devuelta</th>
                <th>Estatus</th>
                <th class="col--num">Piezas</th>
              </tr>
            </thead>
            <tbody>
              <template v-if="loading">
                <tr v-for="n in skeletonRows" :key="`skeleton-${n}`">
                  <td v-for="col in 7" :key="col"><span class="skeleton"></span></td>
                </tr>
              </template>
              <template v-else>
                <tr v-for="loan in loans" :key="loan.id">
                  <td>
                    <span class="barcode-pill" role="button" tabindex="0" @click="openDetail(loan)" @keyup.enter="openDetail(loan)">
                      {{ loan.voucher_number }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(loan.delivered_at) }}</td>
                  <td class="cell-strong">{{ loan.received_by_name || '—' }}</td>
                  <td :class="{ 'cell-muted': !loan.expected_return_at }">{{ formatDateTime(loan.expected_return_at) }}</td>
                  <td :class="{ 'cell-muted': !loan.returned_at }">{{ formatDateTime(loan.returned_at) }}</td>
                  <td><span class="status-pill" :class="statusClass(loan)">{{ loan.status_label }}</span></td>
                  <td class="col--num">{{ loan.items.length }}</td>
                </tr>
                <tr v-if="loans.length === 0">
                  <td colspan="7" class="empty-row">
                    <div class="empty-state">
                      <span class="empty-state__icon">🔧</span>
                      <span class="empty-state__title">No se encontraron préstamos</span>
                      <span class="empty-state__hint">Ajusta los filtros o registra una entrega.</span>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
        <div class="pagination-container">
          <button @click="goToPage(currentPage - 1)" :disabled="currentPage === 1" aria-label="Página anterior">‹</button>
          <span
            v-for="(pageNumber, index) in pageNumbers"
            :key="`${pageNumber}-${index}`"
            :class="{ active: pageNumber === currentPage, ellipsis: pageNumber === ellipsis }"
            @click="goToPage(pageNumber)"
          >
            {{ pageNumber }}
          </span>
          <button @click="goToPage(currentPage + 1)" :disabled="currentPage >= totalPages" aria-label="Página siguiente">›</button>
          <span class="pagination-summary">{{ totalCount }} resultados</span>
        </div>
      </div>
    </div>

    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal-card modal-card--wide" role="dialog" aria-modal="true" aria-labelledby="ph-create-title">
        <h3 id="ph-create-title">Nuevo vale de préstamo</h3>
        <div class="modal-body">
          <div class="modal-grid">
            <div class="form-input-group">
              <label for="ph-deliver">Entrega almacén *</label>
              <select id="ph-deliver" v-model="form.delivered_by">
                <option value="">Seleccione</option>
                <option v-for="emp in employees" :key="`d-${emp.id}`" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="ph-receive">Recibe mecánico *</label>
              <select id="ph-receive" v-model="form.received_by">
                <option value="">Seleccione</option>
                <option v-for="emp in employees" :key="`r-${emp.id}`" :value="emp.id">
                  {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
                </option>
              </select>
            </div>
            <div class="form-input-group">
              <label for="ph-out">Hora de entrega *</label>
              <input id="ph-out" type="datetime-local" v-model="form.delivered_at" />
            </div>
            <div class="form-input-group">
              <label for="ph-due">Devolución esperada</label>
              <input id="ph-due" type="datetime-local" v-model="form.expected_return_at" />
            </div>
            <div class="form-input-group">
              <label for="ph-order">Orden de mantenimiento</label>
              <input id="ph-order" type="text" v-model="form.order_number" placeholder="Opcional" />
            </div>
            <div class="form-input-group form-input-group--full">
              <label for="ph-comments">Comentarios</label>
              <textarea id="ph-comments" v-model="form.comments" rows="2"></textarea>
            </div>
          </div>
          <div class="items-section">
            <div class="items-header">
              <h4>Herramientas</h4>
              <button class="mini-button" type="button" @click="addItem">Agregar herramienta</button>
            </div>
            <div v-for="(item, index) in form.items" :key="index" class="item-row">
              <div class="part-suggest">
                <input
                  type="text"
                  v-model="item.query"
                  placeholder="Nombre o código"
                  autocomplete="off"
                  @input="onPartQuery(item)"
                  @keydown.down.prevent="moveSuggestion(item, 1)"
                  @keydown.up.prevent="moveSuggestion(item, -1)"
                  @keydown.enter.prevent="confirmSuggestion(item)"
                  @blur="hideSuggestions(item)"
                />
                <ul v-if="item.showSuggestions && item.suggestions.length" class="suggest-list">
                  <li v-for="(part, suggestionIndex) in item.suggestions" :key="part.id">
                    <button
                      type="button"
                      :class="{ hovered: suggestionIndex === item.highlight }"
                      @mousedown.prevent="selectPart(item, part)"
                    >
                      {{ part.barcode }} — {{ part.name }} (stock: {{ formatNumber(part.current_stock) }})
                    </button>
                  </li>
                </ul>
              </div>
              <input type="number" min="0.01" step="0.01" v-model.number="item.quantity" placeholder="Cantidad" aria-label="Cantidad" />
              <button class="mini-button mini-button--danger" type="button" aria-label="Quitar" @click="form.items.splice(index, 1)">✕</button>
            </div>
          </div>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="closeCreateModal">Cancelar</button>
          <button class="action-button action-primary" @click="saveLoan" :disabled="saving">
            {{ saving ? 'Guardando...' : 'Guardar vale' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="detail" class="modal-overlay" @click.self="detail = null">
      <div class="modal-card modal-card--wide" role="dialog" aria-modal="true" aria-labelledby="ph-detail-title">
        <h3 id="ph-detail-title">Vale de préstamo {{ detail.voucher_number }}</h3>
        <div class="modal-body">
          <div class="detail-meta">
            <div>
              <span class="detail-meta__label">Estatus</span>
              <span class="detail-meta__value"><span class="status-pill" :class="statusClass(detail)">{{ detail.status_label }}</span></span>
            </div>
            <div>
              <span class="detail-meta__label">Entrega</span>
              <span class="detail-meta__value">{{ formatDateTime(detail.delivered_at) }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Entregó</span>
              <span class="detail-meta__value">{{ detail.delivered_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Recibe</span>
              <span class="detail-meta__value">{{ detail.received_by_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Devolución esperada</span>
              <span class="detail-meta__value">{{ formatDateTime(detail.expected_return_at) }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Devuelta</span>
              <span class="detail-meta__value">{{ formatDateTime(detail.returned_at) }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Recibió de vuelta</span>
              <span class="detail-meta__value">{{ detail.returned_to_name || '—' }}</span>
            </div>
            <div>
              <span class="detail-meta__label">Orden</span>
              <span class="detail-meta__value">{{ detail.maintenance_order_number || 'Sin vincular' }}</span>
            </div>
            <div v-if="detail.comments" class="detail-meta--full">
              <span class="detail-meta__label">Comentarios</span>
              <span class="detail-meta__value">{{ detail.comments }}</span>
            </div>
          </div>
          <h4 class="section-title">Herramientas</h4>
          <table class="data-table data-table--embedded">
            <thead>
              <tr>
                <th>Código</th>
                <th>Herramienta</th>
                <th class="col--num">Cantidad</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in detail.items" :key="item.id">
                <td>{{ item.part_barcode }}</td>
                <td class="cell-strong">{{ item.part_name }}</td>
                <td class="col--num">{{ formatNumber(item.quantity) }} {{ item.part_unit_of_measure || '' }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="canCreate && detail.status === 'loaned'" class="form-input-group" style="margin-top: 16px; max-width: 360px;">
            <label for="ph-return-to">Recibe la devolución *</label>
            <select id="ph-return-to" v-model="returnTo">
              <option value="">Seleccione</option>
              <option v-for="emp in employees" :key="`back-${emp.id}`" :value="emp.id">
                {{ emp.employee_ident }} - {{ emp.employee_first_name }} {{ emp.employee_last_name }}
              </option>
            </select>
          </div>
        </div>
        <div class="modal-actions">
          <button class="action-button" @click="detail = null">Cerrar</button>
          <button class="action-button" @click="printTicketCurrent">Imprimir ticket</button>
          <button class="action-button" @click="printCurrent">Imprimir</button>
          <button v-if="canCreate && detail.status === 'loaned'" class="action-button" @click="cancelLoan" :disabled="saving">Cancelar</button>
          <button v-if="canCreate && detail.status === 'loaned'" class="action-button action-primary" @click="returnLoan" :disabled="saving">Registrar devolución</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Swal from '@/utils/warehouseSwal'
import { printToolLoan } from '@/utils/printVoucher'
import { printToolLoanTicket } from '@/utils/printTicket'
import { normalizeScannedCode } from '@/utils/scannedCode'
import warehouseTable from '@/mixins/warehouseTable'

export default {
  name: 'WarehouseToolLoans',
  mixins: [warehouseTable],
  data() {
    return {
      loans: [],
      loading: false,
      saving: false,
      currentPage: 1,
      totalPages: 1,
      totalCount: 0,
      pageSize: 20,
      filters: { voucher_number: '', part: '', mechanic: '', status: '', start_date: '', end_date: '' },
      employees: [],
      showCreateModal: false,
      form: {
        delivered_by: '',
        received_by: '',
        delivered_at: '',
        expected_return_at: '',
        order_number: '',
        comments: '',
        items: [],
      },
      detail: null,
      returnTo: '',
      permissions: [],
    }
  },
  computed: {
    canCreate() {
      return this.hasPerm('maintenance.create_tool_loan')
    },
  },
  created() {
    this.loadPermissions()
  },
  mounted() {
    this.fetchLoans(1)
    this.fetchEmployees()
  },
  methods: {
    emptyFilters() {
      return { voucher_number: '', part: '', mechanic: '', status: '', start_date: '', end_date: '' }
    },
    emptyItem() {
      return { part: '', query: '', quantity: 1, suggestions: [], showSuggestions: false, highlight: 0 }
    },
    emptyForm() {
      return {
        delivered_by: '',
        received_by: '',
        delivered_at: this.nowLocal(),
        expected_return_at: '',
        order_number: '',
        comments: '',
        items: [this.emptyItem()],
      }
    },
    nowLocal() {
      const date = new Date()
      const pad = (value) => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
    },
    loadPermissions() {
      try {
        this.permissions = JSON.parse(localStorage.getItem('userPermissions')) || []
      } catch (error) {
        this.permissions = []
      }
    },
    hasPerm(perm) {
      return this.permissions.includes(perm) || this.permissions.includes(`access_control.${perm}`)
    },
    backMainPanel() {
      this.$router.push('/main-panel')
    },
    backWarehouse() {
      this.$router.push('/warehouse')
    },
    statusClass(loan) {
      if (loan.is_overdue) return 'status-pill--danger'
      if (loan.status === 'returned') return 'status-pill--ok'
      if (loan.status === 'cancelled') return 'status-pill--danger'
      return 'status-pill--warn'
    },
    formatNumber(value) {
      const num = Number(value)
      if (Number.isNaN(num)) return '—'
      return num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDateTime(value) {
      if (!value) return '—'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '—'
      return date.toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' })
    },
    applyFilters() {
      this.filters.voucher_number = normalizeScannedCode(this.filters.voucher_number)
      this.filters.part = normalizeScannedCode(this.filters.part)
      this.fetchLoans(1)
    },
    clearFilters() {
      this.filters = this.emptyFilters()
      this.fetchLoans(1)
    },
    goToPage(pageNumber) {
      if (typeof pageNumber !== 'number') return
      if (pageNumber < 1 || pageNumber > this.totalPages || pageNumber === this.currentPage) return
      this.fetchLoans(pageNumber)
    },
    async fetchLoans(page) {
      const apiDirectory = process.env.API_DIRECTORY
      this.loading = true
      try {
        const response = await axios.post(
          `${apiDirectory}/warehouse/get-tool-loans-filtered?page=${page}&page_size=${this.pageSize}`,
          this.filters,
          { headers: { 'Content-Type': 'application/json' } },
        )
        this.loans = response.data.results || []
        this.totalCount = response.data.count || 0
        this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize))
        this.currentPage = page
      } catch (error) {
        console.error('Failed to fetch tool loans:', error)
        Swal.fire('Error', 'No se pudieron cargar los préstamos.', 'error')
      } finally {
        this.loading = false
      }
    },
    async fetchEmployees() {
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/employees-for-forms`)
        this.employees = response.data.results || []
      } catch (error) {
        console.error('Failed to fetch employees:', error)
      }
    },
    openCreateModal() {
      this.form = this.emptyForm()
      this.showCreateModal = true
    },
    closeCreateModal() {
      this.showCreateModal = false
    },
    addItem() {
      this.form.items.push(this.emptyItem())
    },
    onPartQuery(item) {
      const normalized = normalizeScannedCode(item.query)
      if (normalized !== item.query) item.query = normalized
      item.part = ''
      item.highlight = 0
      clearTimeout(item.searchTimer)
      const query = (item.query || '').trim()
      if (!query) {
        item.suggestions = []
        item.showSuggestions = false
        return
      }
      item.searchTimer = setTimeout(() => this.fetchSuggestions(item, query), 180)
    },
    async fetchSuggestions(item, query) {
      const token = (item.searchToken || 0) + 1
      item.searchToken = token
      const apiDirectory = process.env.API_DIRECTORY
      try {
        const response = await axios.get(`${apiDirectory}/warehouse/parts-for-forms`, {
          params: { search: query, limit: 8 },
        })
        if (item.searchToken !== token) return
        item.suggestions = response.data.results || []
        item.showSuggestions = item.suggestions.length > 0
      } catch (error) {
        if (item.searchToken !== token) return
        item.suggestions = []
        item.showSuggestions = false
      }
    },
    moveSuggestion(item, step) {
      if (!item.suggestions.length) return
      const next = item.highlight + step
      const last = item.suggestions.length - 1
      item.highlight = next < 0 ? last : next > last ? 0 : next
    },
    confirmSuggestion(item) {
      const part = item.suggestions[item.highlight]
      if (part) this.selectPart(item, part)
    },
    selectPart(item, part) {
      item.part = part.id
      item.query = `${part.barcode} — ${part.name}`
      item.suggestions = []
      item.showSuggestions = false
    },
    hideSuggestions(item) {
      setTimeout(() => { item.showSuggestions = false }, 120)
    },
    async saveLoan() {
      if (!this.form.delivered_by || !this.form.received_by || !this.form.delivered_at) {
        Swal.fire('Falta información', 'Indica quién entrega, quién recibe y la hora de entrega.', 'warning')
        return
      }
      const unfinished = this.form.items.some((item) => (item.query || '').trim() && !item.part)
      if (unfinished) {
        Swal.fire('Falta información', 'Elige la herramienta desde las sugerencias.', 'warning')
        return
      }
      const items = this.form.items.filter((item) => item.part && Number(item.quantity) > 0)
      if (!items.length) {
        Swal.fire('Falta información', 'Agrega al menos una herramienta.', 'warning')
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.saving = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/create-tool-loan`, {
          delivered_by: this.form.delivered_by,
          received_by: this.form.received_by,
          delivered_at: this.form.delivered_at,
          expected_return_at: this.form.expected_return_at || null,
          order_number: this.form.order_number || null,
          comments: this.form.comments || null,
          items: items.map((item) => ({ part: item.part, quantity: item.quantity })),
        }, { headers: { 'Content-Type': 'application/json' } })
        this.showCreateModal = false
        const result = await Swal.fire({
          title: 'Vale de préstamo creado',
          text: `Folio ${response.data.voucher_number}. ¿Deseas imprimirlo?`,
          icon: 'success',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Imprimir',
          denyButtonText: 'Imprimir ticket',
          cancelButtonText: 'Después',
        })
        if (result.isConfirmed) printToolLoan(response.data)
        if (result.isDenied) printToolLoanTicket(response.data)
        this.fetchLoans(1)
      } catch (error) {
        console.error('Failed to create tool loan:', error)
        Swal.fire('Error', error.response?.data?.error || 'No se pudo crear el vale.', 'error')
      } finally {
        this.saving = false
      }
    },
    openDetail(loan) {
      this.detail = loan
      this.returnTo = ''
    },
    printCurrent() {
      printToolLoan(this.detail)
    },
    printTicketCurrent() {
      printToolLoanTicket(this.detail)
    },
    async returnLoan() {
      if (!this.returnTo) {
        Swal.fire('Falta información', 'Selecciona quién recibe la herramienta de vuelta.', 'warning')
        return
      }
      const apiDirectory = process.env.API_DIRECTORY
      this.saving = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/tool-loans/${this.detail.id}/return/`, {
          returned_to: this.returnTo,
        }, { headers: { 'Content-Type': 'application/json' } })
        this.detail = response.data
        await this.fetchLoans(this.currentPage)
        Swal.fire('Devuelta', 'La herramienta volvió a almacén.', 'success')
      } catch (error) {
        Swal.fire('Error', error.response?.data?.error || 'No se pudo registrar la devolución.', 'error')
      } finally {
        this.saving = false
      }
    },
    async cancelLoan() {
      const confirm = await Swal.fire({
        title: '¿Cancelar el préstamo?',
        text: 'La existencia de la herramienta se repondrá.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Cancelar vale',
        cancelButtonText: 'Volver',
      })
      if (!confirm.isConfirmed) return
      const apiDirectory = process.env.API_DIRECTORY
      this.saving = true
      try {
        const response = await axios.post(`${apiDirectory}/warehouse/tool-loans/${this.detail.id}/cancel/`)
        this.detail = response.data
        await this.fetchLoans(this.currentPage)
      } catch (error) {
        Swal.fire('Error', error.response?.data?.error || 'No se pudo cancelar el vale.', 'error')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped src="@/styles/warehouse.css"></style>
